import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import { entidadService } from '../services/api';
import '../styles/datatable.css';
import { getErrorDetail } from '../utils/errorHelper';

const TIPOS_DOC = [
  { codigo: '6', label: 'RUC' },
  { codigo: '1', label: 'DNI' },
  { codigo: '4', label: 'Carnet de Extranjeria' },
  { codigo: '7', label: 'Pasaporte' },
  { codigo: '0', label: 'Otros' },
];

const getTipoDocLabel = (codigo) => {
  const t = TIPOS_DOC.find(d => d.codigo === codigo);
  return t ? t.label : codigo || '--';
};

const Entidades = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [consultando, setConsultando] = useState(false);

  const emptyForm = { ruc: '', razon_social: '', direccion: '', correo1: '', telefono_contacto: '', celular: '', persona_contacto: '', tipo_documento: '6' };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await entidadService.getAll();
      setRecords(res.data);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar entidades.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    window._dtImportHandler = async (rows) => {
      let ok = 0, fail = 0;
      const total = rows.length;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const razon_social = row['Razon Social'] || row['razon_social'] || '';
        let errorMsg = null;
        if (!razon_social) {
          fail++; errorMsg = `Fila ${i + 1}: Razon Social vacia (obligatoria)`;
        } else {
          try {
            await entidadService.create({
              ruc: row['RUC'] || row['ruc'] || null,
              razon_social,
              direccion: row['Direccion'] || row['direccion'] || null,
              correo1: row['Correo'] || row['correo1'] || null,
              telefono_contacto: row['Telefono'] || row['telefono_contacto'] || null,
              celular: row['Celular'] || row['celular'] || null,
              persona_contacto: row['Contacto'] || row['persona_contacto'] || null,
              tipo_documento: row['Tipo Doc.'] || row['tipo_documento'] || null,
            });
            ok++;
          } catch (err) {
            fail++;
            errorMsg = `Fila ${i + 1} ("${razon_social}"): ${getErrorDetail(err, 'Error al crear entidad')}`;
          }
        }
        if (window._dtImportProgress) window._dtImportProgress(i + 1, total, ok, fail, errorMsg);
      }
      fetchData();
    };
    return () => { window._dtImportHandler = null; };
  }, [fetchData]);

  const openCreate = () => { setForm(emptyForm); setEditing(null); setShowModal(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      ruc: r.ruc || '', razon_social: r.razon_social || '',
      direccion: r.direccion || '', correo1: r.correo1 || '', telefono_contacto: r.telefono_contacto || '',
      celular: r.celular || '',
      persona_contacto: r.persona_contacto || '', tipo_documento: r.tipo_documento || '6',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.razon_social) { setAlert({ type: 'warning', message: 'Razon social es obligatoria.' }); return; }
    const data = {};
    Object.entries(form).forEach(([k, v]) => { data[k] = v === '' ? null : v; });
    try {
      if (editing) await entidadService.update(editing.id_entidad, data);
      else await entidadService.create(data);
      setShowModal(false); fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Error al guardar.' });
    }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar Entidad', message: `Eliminar "${r.razon_social}"?`,
      onConfirm: async () => { setConfirm(null); try { await entidadService.delete(r.id_entidad); setToast('Entidad eliminada'); fetchData(); } catch { setAlert({ type: 'error', message: 'Error al eliminar.' }); } },
    });
  };

  const handleConsultarRuc = async () => {
    if (!form.ruc || form.ruc.length !== 11) {
      setAlert({ type: 'warning', message: 'Ingrese un RUC valido de 11 digitos.' });
      return;
    }
    setConsultando(true);
    try {
      const res = await entidadService.consultarRuc(form.ruc);
      setForm(prev => ({
        ...prev,
        razon_social: res.data.razon_social || prev.razon_social,
        direccion: res.data.direccion || prev.direccion,
      }));
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'No se encontraron datos para ese RUC.' });
    } finally {
      setConsultando(false);
    }
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const isRuc = form.tipo_documento === '6';

  const columns = [
    { key: 'tipo_documento', label: 'Tipo Doc.', width: 130, render: (r) => getTipoDocLabel(r.tipo_documento) },
    { key: 'ruc', label: 'Nro. Doc.', width: 130, style: { fontWeight: 600 } },
    { key: 'razon_social', label: 'Razon Social / Nombre' },
    { key: 'telefono_contacto', label: 'Telefono', width: 120 },
    { key: 'celular', label: 'Celular', width: 120 },
    { key: 'persona_contacto', label: 'Contacto', width: 150 },
  ];

  const importCols = [
    { key: 'Tipo Doc.', label: 'Tipo Doc.', required: false, example: '6', description: 'Codigo tipo documento: 6=RUC, 1=DNI, 4=CE, 7=Pasaporte, 0=Otros' },
    { key: 'RUC', label: 'RUC', required: false, example: '20100000001', description: 'Numero de documento (RUC, DNI, etc.)' },
    { key: 'Razon Social', label: 'Razon Social', required: true, example: 'Empresa Ejemplo S.A.C.', description: 'Razon social o nombre completo' },
    { key: 'Direccion', label: 'Direccion', required: false, example: 'Av. Principal 123, Lima', description: 'Direccion fiscal o domicilio' },
    { key: 'Correo', label: 'Correo', required: false, example: 'contacto@empresa.com', description: 'Correo electronico principal' },
    { key: 'Telefono', label: 'Telefono', required: false, example: '01-2345678', description: 'Telefono de contacto' },
    { key: 'Celular', label: 'Celular', required: false, example: '987654321', description: 'Numero de celular' },
    { key: 'Contacto', label: 'Contacto', required: false, example: 'Juan Perez', description: 'Nombre de la persona de contacto' },
  ];

  return (
    <Layout title="Proveedores / Clientes" subtitle="Entidades">
      <div className="page-header">
        <h2>Proveedores / Clientes</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ Nuevo</button>
      </div>

      <DataTable columns={columns} data={records} idField="id_entidad" onEdit={openEdit} onDelete={handleDelete}
        loading={loading} emptyMessage="No hay entidades registradas" exportFileName="entidades" importColumns={importCols} />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:580}} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <h2>{editing ? 'Editar Entidad' : 'Entidad'}</h2>
                {!editing && <span className="modal-header-badge"><svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/></svg></span>}
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <label>Tipo de Documento</label>
                  <select name="tipo_documento" value={form.tipo_documento} onChange={handleChange}>
                    {TIPOS_DOC.map(t => <option key={t.codigo} value={t.codigo}>{t.label}</option>)}
                  </select>
                </div>
                <div style={{display:'grid',gridTemplateColumns: isRuc ? '140px 1fr auto' : '140px 1fr',gap:'0 12px', alignItems:'flex-end'}}>
                  <div className="form-row"><label>Nro. Documento</label><input name="ruc" value={form.ruc} onChange={handleChange} placeholder={isRuc ? 'RUC 11 digitos' : 'Nro. documento'} maxLength={isRuc ? 11 : 20} /></div>
                  <div className="form-row"><label>Razon Social / Nombre *</label><input name="razon_social" value={form.razon_social} onChange={handleChange} placeholder="Razon social o nombre" /></div>
                  {isRuc && (
                    <button type="button" className="btn btn-secondary" onClick={handleConsultarRuc} disabled={consultando} style={{marginBottom:18, whiteSpace:'nowrap'}}>
                      {consultando ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{animation:'spin 1s linear infinite'}}>
                          <path d="M21 12a9 9 0 11-6.219-8.56"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/></svg>
                      )}
                    </button>
                  )}
                </div>
                <div className="form-row"><label>Direccion</label><input name="direccion" value={form.direccion} onChange={handleChange} placeholder="Direccion" /></div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'0 16px'}}>
                  <div className="form-row"><label>Correo</label><input name="correo1" value={form.correo1} onChange={handleChange} placeholder="correo@ejemplo.com" /></div>
                  <div className="form-row"><label>Telefono de Contacto</label><input name="telefono_contacto" value={form.telefono_contacto} onChange={handleChange} placeholder="Telefono" /></div>
                  <div className="form-row"><label>Celular</label><input name="celular" value={form.celular} onChange={handleChange} placeholder="Celular" /></div>
                </div>
                <div className="form-row"><label>Persona de Contacto</label><input name="persona_contacto" value={form.persona_contacto} onChange={handleChange} placeholder="Nombre del contacto" /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
      {confirm && <Confirm title={confirm.title} message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </Layout>
  );
};

export default Entidades;
